import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RotateCcw, Trash2, ScanLine, Type } from 'lucide-react';
import { apiAIFetch } from '../../api';

// Load Tesseract.js from CDN on demand (no npm install required)
let tesseractPromise = null;
const loadTesseract = () => {
  if (tesseractPromise) return tesseractPromise;
  tesseractPromise = new Promise((resolve, reject) => {
    if (window.Tesseract) { resolve(window.Tesseract); return; }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/tesseract.js@4/dist/tesseract.min.js';
    script.onload = () => resolve(window.Tesseract);
    script.onerror = () => { tesseractPromise = null; reject(new Error('Failed to load Tesseract')); };
    document.head.appendChild(script);
  });
  return tesseractPromise;
};

const getPos = (e, canvas) => {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  const src = e.touches ? e.touches[0] : e;
  return { x: (src.clientX - rect.left) * scaleX, y: (src.clientY - rect.top) * scaleY };
};

const CANVAS_W = 580;
const CANVAS_H = 160;

const DrawMathCanvas = ({ onInsert, onClose }) => {
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const drawRafRef = useRef(null);
  const strokesRef = useRef([]); // array of point arrays (committed)
  const currentStrokeRef = useRef(null);
  const [strokeCount, setStrokeCount] = useState(0); // triggers re-enable of buttons
  const [mode, setMode] = useState('draw'); // 'draw' | 'type'
  const [typeText, setTypeText] = useState('');
  const [status, setStatus] = useState('idle'); // idle | recognizing | done | error
  const [recognized, setRecognized] = useState('');

  // --- Canvas drawing ---
  const paintCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    [...strokesRef.current, currentStrokeRef.current].filter(Boolean).forEach((pts) => {
      if (pts.length < 2) return;
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      pts.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
      ctx.stroke();
    });
  }, []);

  const scheduleRepaint = useCallback(() => {
    if (drawRafRef.current != null) return;
    drawRafRef.current = requestAnimationFrame(() => {
      drawRafRef.current = null;
      paintCanvas();
    });
  }, [paintCanvas]);

  useEffect(() => {
    paintCanvas();
    return () => {
      if (drawRafRef.current != null) cancelAnimationFrame(drawRafRef.current);
    };
  }, [paintCanvas]);

  const handlePointerDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    isDrawingRef.current = true;
    currentStrokeRef.current = [getPos(e, canvasRef.current)];
    scheduleRepaint();
  }, [scheduleRepaint]);

  const handlePointerMove = useCallback((e) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    const events = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : [e];
    const pts = events.map((ev) => getPos(ev, canvasRef.current));
    currentStrokeRef.current = [...(currentStrokeRef.current || []), ...pts];
    scheduleRepaint();
  }, [scheduleRepaint]);

  const handlePointerUp = useCallback((e) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    isDrawingRef.current = false;
    if (currentStrokeRef.current && currentStrokeRef.current.length > 0) {
      strokesRef.current = [...strokesRef.current, currentStrokeRef.current];
      setStrokeCount(strokesRef.current.length);
    }
    currentStrokeRef.current = null;
    scheduleRepaint();
  }, [scheduleRepaint]);

  const handleUndo = () => {
    strokesRef.current = strokesRef.current.slice(0, -1);
    setStrokeCount(strokesRef.current.length);
    setStatus('idle');
    setRecognized('');
    scheduleRepaint();
  };

  const handleClear = () => {
    strokesRef.current = [];
    currentStrokeRef.current = null;
    setStrokeCount(0);
    setStatus('idle');
    setRecognized('');
    scheduleRepaint();
  };

  // --- Recognition ---
  const runRecognize = async () => {
    const canvas = canvasRef.current;
    if (!canvas || strokesRef.current.length === 0) return;
    setStatus('recognizing');
    setRecognized('');

    try {
      // Scale up 2× for better OCR accuracy
      const offscreen = document.createElement('canvas');
      offscreen.width = CANVAS_W * 2;
      offscreen.height = CANVAS_H * 2;
      const octx = offscreen.getContext('2d');
      octx.fillStyle = '#ffffff';
      octx.fillRect(0, 0, offscreen.width, offscreen.height);
      octx.drawImage(canvas, 0, 0, offscreen.width, offscreen.height);

      const Tesseract = await loadTesseract();
      const { data: { text } } = await Tesseract.recognize(
        offscreen.toDataURL('image/png'),
        'eng',
        {
          logger: () => {},
          workerPath: 'https://cdn.jsdelivr.net/npm/tesseract.js@4/dist/worker.min.js',
          langPath: 'https://tessdata.projectnaptha.com/4.0.0',
          corePath: 'https://cdn.jsdelivr.net/npm/tesseract.js-core@4/tesseract-core.wasm.js',
        }
      );
      const clean = text.trim().replace(/\n+/g, ' ').replace(/\s{2,}/g, ' ').trim();
      if (clean) {
        setRecognized(clean);
        setStatus('done');
      } else {
        setStatus('error');
      }
    } catch (err) {
      console.error('DrawMathCanvas OCR error:', err);
      setStatus('error');
    }
  };

  // After OCR gives us text, optionally run it through format-latex for proper LaTeX
  const convertToLatex = async (rawText) => {
    try {
      const res = await apiAIFetch('/api/format-latex', 'whiteboard.format_latex', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: rawText }),
      });
      if (res.ok) {
        const data = await res.json();
        return data.latex || rawText;
      }
    } catch (_) {}
    return rawText;
  };

  const handleUseRaw = () => {
    onInsert(recognized);
  };

  const handleUseFormatted = async () => {
    setStatus('recognizing');
    const latex = await convertToLatex(recognized);
    onInsert(latex);
  };

  // --- Type mode ---
  const handleTypeConvert = async () => {
    if (!typeText.trim()) return;
    setStatus('recognizing');
    const latex = await convertToLatex(typeText.trim());
    onInsert(latex);
  };

  return (
    <div
      className="wb-draw-math"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="wb-draw-math-header">
        <div className="wb-draw-math-tabs">
          <button
            className={`wb-draw-tab${mode === 'draw' ? ' active' : ''}`}
            onClick={() => { setMode('draw'); setStatus('idle'); setRecognized(''); }}
          >
            <ScanLine size={13} /> Draw
          </button>
          <button
            className={`wb-draw-tab${mode === 'type' ? ' active' : ''}`}
            onClick={() => { setMode('type'); setStatus('idle'); setRecognized(''); }}
          >
            <Type size={13} /> Type
          </button>
        </div>
        <button className="wb-draw-math-close" onClick={onClose} title="Close">×</button>
      </div>

      {mode === 'draw' && (
        <>
          <p className="wb-draw-hint">Write your equation on the canvas, then click Recognize.</p>
          <canvas
            ref={canvasRef}
            className="wb-draw-canvas"
            width={CANVAS_W}
            height={CANVAS_H}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
            onPointerCancel={handlePointerUp}
            style={{ touchAction: 'none' }}
          />
          <div className="wb-draw-controls">
            <button className="wb-draw-btn" onClick={handleUndo} disabled={strokeCount === 0} title="Undo last stroke">
              <RotateCcw size={13} /> Undo
            </button>
            <button className="wb-draw-btn" onClick={handleClear} disabled={strokeCount === 0} title="Clear canvas">
              <Trash2 size={13} /> Clear
            </button>
            <button
              className="wb-magic-button"
              onClick={runRecognize}
              disabled={strokeCount === 0 || status === 'recognizing'}
            >
              {status === 'recognizing'
                ? <><span className="wb-spinner" /> Recognizing...</>
                : <><ScanLine size={13} /> Recognize</>}
            </button>
          </div>

          {status === 'done' && (
            <div className="wb-draw-result">
              <span className="wb-draw-result-label">Recognized text:</span>
              <input
                className="wb-draw-result-input"
                value={recognized}
                onChange={(e) => setRecognized(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              />
              <div className="wb-draw-result-actions">
                <button className="wb-templates-btn" onClick={handleUseRaw} title="Insert recognized text as-is">
                  Insert as text
                </button>
                <button className="wb-magic-button" onClick={handleUseFormatted} title="Convert to LaTeX first, then insert">
                  Convert to LaTeX →
                </button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="wb-draw-result wb-draw-error">
              Could not recognize the drawing. Try writing more clearly, or switch to Type mode.
            </div>
          )}
        </>
      )}

      {mode === 'type' && (
        <>
          <p className="wb-draw-hint">Type your equation in natural language or LaTeX, then convert.</p>
          <textarea
            className="wb-math-input"
            value={typeText}
            onChange={(e) => setTypeText(e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            placeholder="e.g. 'integral of x squared from 0 to 1' or 'F = ma'"
            rows={3}
          />
          <div className="wb-draw-controls">
            <button
              className="wb-magic-button"
              onClick={handleTypeConvert}
              disabled={!typeText.trim() || status === 'recognizing'}
            >
              {status === 'recognizing'
                ? <><span className="wb-spinner" /> Converting...</>
                : 'Convert to LaTeX →'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default DrawMathCanvas;
